/**
 * /pricing — SMB-friendly copy (apps + syncs, not connectors + pipelines).
 */

export const PRICING_LANDING = {
    hero: {
        eyebrow: 'Transparent pricing',
        headline: 'Simple two-part pricing',
        lead: '$15 per app you connect each month, plus $2 for every million rows synced.',
    },

    summary: {
        rowUnit: 'per million rows synced',
        appUnit: 'per month per app',
        description:
            'Connect the tools you already use—Shopify, QuickBooks, spreadsheets, and more. Pay for each app once, then pay for the rows that actually sync.',
    },

    /** Home page typical-bill example. */
    homeExampleSummary: {
        headline: 'Most teams pay $32–$50/month',
        lines: [
            '$30/month for two apps—e.g. $15 for LinkedIn Ads and $15 for Google Sheets.',
            'About $2/month for rows at typical campaign volume (under a million rows synced).',
            'No long-term contract—cancel anytime.',
        ],
        supporting:
            'Higher campaign volume means more rows synced; each extra million rows adds $2 to your bill.',
    },

    included: [
        'Unlimited team members',
        'Unlimited syncs per app',
        'Connect as many accounts as you need per app',
        'Sync on the schedule you choose',
        'New apps added regularly',
        'Clear activity history',
        'Free setup help',
        'Transparent billing',
        'HIPAA and SOC 2 Type II compliant',
        'Easy to set up—no code required',
    ],

    calculator: {
        eyebrow: 'Price calculator',
        headline: 'Estimate your monthly cost',
        lead: 'Slide to match your apps and how many rows you sync each month.',
    },

    slider: {
        appsLabel: 'Connected apps',
        appsDescription: '$15/month per app · Connect every account you need for that app',
        rowsLabel: 'Rows synced each month',
        rowsDescription: '$2 per million rows synced',
        priceLabel: 'Your estimated price',
        estimateIncludes: [
            'Unlimited team members included—no per-seat fees.',
            'Unlimited accounts per app—every store, org, or login for one app price.',
        ],
        breakdown: (appCount, appCost, rowCost, millionsBilled) =>
            `Breakdown: $${appCost} (${appCount} app${appCount !== 1 ? 's' : ''}) + $${rowCost} (${millionsBilled} million rows synced)`,
        competitorFootnote: '*Based on typical competitor pricing: $19/app + $20.34 per million rows.',
        competitorWarnings: [
            'Pricing can be even higher for more user seats.',
            'Extra accounts per app often cost more with competitors.',
        ],
    },

    compare: {
        headline: 'How we compare',
        lead: 'See the difference between predictable pricing and surprise bills.',
        rows: [
            {
                category: 'Pricing',
                dataConnector: '$15 per app + $2 per million rows synced. No surprise tiers.',
                otherPlatforms: 'Surprise invoices and usage tiers',
            },
            {
                category: 'Team access',
                dataConnector: 'Unlimited team members—no per-seat fees.',
                otherPlatforms: 'Pay per seat',
            },
            {
                category: 'Accounts per app',
                dataConnector: 'Connect every account you need for one app price.',
                otherPlatforms: 'Extra fees and throttled tiers',
            },
            {
                category: 'New apps',
                dataConnector: 'New apps usually ready in 3–7 days.',
                otherPlatforms: 'Wait months—or never get built',
            },
            {
                category: 'App coverage',
                dataConnector: 'If it has an API, we can usually connect it.',
                otherPlatforms: 'Only a short list of platforms',
            },
            {
                category: 'Billing',
                dataConnector: 'Month-to-month. Cancel anytime.',
                otherPlatforms: 'Annual contracts and minimums',
            },
            {
                category: 'Ease of use',
                dataConnector: 'Set up in minutes—no config files.',
                otherPlatforms: 'Steep learning curve',
            },
            {
                category: 'Time to value',
                dataConnector: 'Live syncs in minutes, not months.',
                otherPlatforms: 'Long setup and approvals',
            },
            {
                category: 'Roadmap',
                dataConnector: 'Customers help decide what we build next.',
                otherPlatforms: 'Slow internal backlog',
            },
            {
                category: 'Compliance',
                dataConnector: 'SOC 2 Type II and HIPAA compliant.',
                otherPlatforms: 'Often SOC 2-only or extra HIPAA fees',
            },
            {
                category: 'Visibility',
                dataConnector: 'See sync status and usage in real time.',
                otherPlatforms: 'Limited logs unless you pay more',
            },
            {
                category: 'Support',
                dataConnector: 'White-glove help from our team.',
                otherPlatforms: 'Tiered queues and slow responses',
            },
        ],
    },

    seo: {
        title: 'Data Connector Pricing — $15 per App & $2 per Million Rows Synced',
        description:
            'Simple two-part pricing: $15 per month for each app you connect (Shopify, QuickBooks, spreadsheets, and more), plus $2 for every million rows synced. Unlimited accounts per app. Month-to-month, no long-term contract.',
        keywords:
            'data connector pricing, shopify quickbooks sync pricing, app integration pricing, rows synced pricing, transparent pricing, pay per app, small business data sync',
    },

    faq: [
        {
            question: 'How does Data Connector pricing work?',
            answer:
                'Two parts: $15 per month for each app you connect (like Shopify or QuickBooks), and $2 for every million rows synced in that month. You pay once per app—not per login or account.',
        },
        {
            question: 'Do I pay per account or per app?',
            answer:
                'Per app. One $15/month charge covers Shopify (or QuickBooks, Salesforce, etc.) with as many accounts or stores as you need for that app.',
        },
        {
            question: 'How much do rows cost?',
            answer:
                '$2 for each million rows synced in a month. Under a million rows? Row usage is usually about $2 for the month. Each additional million adds another $2.',
        },
        {
            question: 'Do you require long-term contracts?',
            answer:
                'No. Month-to-month billing. Cancel anytime with no setup fees or penalties.',
        },
        {
            question: 'How can I track my usage and costs?',
            answer:
                'Your dashboard shows which apps are connected, how many rows synced, and what you owe—updated in real time so there are no surprise invoices.',
        },
        {
            question: 'Can I set this up myself?',
            answer:
                'Yes. Turn apps on or off, connect accounts, and choose what to sync—no sales call required. Free setup help if you want it.',
        },
        {
            question: 'How fast do syncs run?',
            answer:
                'Syncs run on dedicated infrastructure so your jobs are not stuck behind other customers. Schedules run automatically once you connect your apps.',
        },
        {
            question: 'Are there hidden fees?',
            answer:
                'No. You pay for connected apps and rows synced—that is it. No build fees and no long-term contract.',
        },
        {
            question: 'Can I start with a free trial?',
            answer:
                'Yes. Start a 7-day free trial with no credit card to see your apps stay in sync before you commit.',
        },
        {
            question: 'How do I estimate my monthly bill?',
            answer:
                'Multiply your number of apps by $15, then add $2 for each million rows you sync. Use the calculator on this page for a quick estimate.',
        },
    ],
};
